import * as React from 'react';
import debounce from 'lodash.debounce';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from "@mui/material/TextField";
import Skeleton from '@mui/material/Skeleton';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from '@mui/icons-material/Search';
import { Grid, InputAdornment, Stack } from '@mui/material';
import SearchListGrid from '../searchListGrid/SearchListGrid';
import { getPrivacyClassificationText, getSecurityClassificationText } from '../../shared/functions';
import {
    getSourcesListSelector,
    getSourceSelector,
    deleteSourceSelector,
    getSourcesListPendingSelector,
    deleteSourcePendingSelector,
} from "../../../store/dataSource/selectors";
import {
    deleteSourceReq,
    fetchSourceDetailsReq,
    fetchSourcesReq,
} from "../../../store/dataSource/actions"
import { IDataSource, IDataSourceDetails, ISearchParam } from '../../../store/dataSource/types';
import { HeadCell, Order } from '../tableGrid/types';
import EnhancedTable from '../tableGrid/tableGrid';
import EnhancedTableToolbar from '../tableGrid/tableToolbar';
import EnhancedTableHead from '../tableGrid/tableHead';
import Row from '../tableGrid/tableRow';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import CustomizedDialogs from '../../atoms/modal/customizedDialogs';
import { IProfile } from "../../../store/userProfile/types";
import { getProfileSelector } from '../../../store/userProfile/selectors';
import { fetchSpokeSelector } from "../../../store/spokes/selectors";
import { fetchSpokesRequest } from "../../../store/spokes/actions";
import { ISpokeDetails, ISpokeSearchParams } from "../../../store/spokes/types";
import {SEARCH_DEBOUNCE_TIME_MILLIS} from "../../../constants/common";
// settings data

const headCells: HeadCell[] = [
    {
        id: 'accessControl',
        numeric: false,
        disablePadding: false,
        label: '',
        width: "20px"
    },
    {
        id: 'description',
        numeric: false,
        disablePadding: false,
        label: 'Description',
        width: "200px",
    },
    {
        id: 'name',
        numeric: false,
        disablePadding: false,
        label: 'Name',
        width: "150px",
    },
    {
        id: 'layerType',
        numeric: false,
        disablePadding: false,
        label: 'Layer Type',
        width: "80px",
    },
    {
        id: 'spokeName',
        numeric: false,
        disablePadding: false,
        label: 'Spoke',
        width: "80px",
    },
];

const filterCells: string[] = [
    'layerType', 'accessControl', 'owner', 'dataStewards', 'spokeName'
]

// end

const descendingComparator = (a: IDataSource, b: IDataSource, orderBy: keyof IDataSource) => {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

const getComparator = (order: Order, orderBy: keyof IDataSource): (
    a: IDataSource,
    b: IDataSource,
) => number => {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
};


interface SourceExplorerProps {
    isAdmin: boolean;
};


const SourceExplorer = (props: SourceExplorerProps) => {

    const { isAdmin, ...other } = props;
    const deletePending = useSelector(deleteSourcePendingSelector)

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [dataRows, setDataRows] = useState<IDataSource[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [order, setOrder] = useState<Order>("asc");
    const [orderBy, setOrderBy] = useState<keyof IDataSource>("description");
    const [rowDetails, setRowDetails] = useState<IDataSourceDetails[]>([]);
    const [selected, setSelected] = useState<readonly string[]>([]);
    const [open, setOpen] = useState(false);
    const [rowID, setRowID] = useState(0);
    const [rowName, setRowName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [deletedIdLoading, setDeletedIdLoading] = useState('')
    const [deletedIds, setDeletedIds] = useState<string[]>([]);
    // Filters-related
    const [layerTypes, setLayerTypes] = useState<string[]>([]);
    const [accessControls, setAccessControls] = useState<string[]>([]);
    const [dataStewards, setDataStewards] = useState<string[]>([]);
    const [owners, setOwners] = useState<string[]>([]);
    const [spokeName, setSpokeName] = useState<string[]>([]);
    const [filters, setFilters] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState(false);

    const selectable: string = "no";
    const allSelectable: boolean = false;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // source list    
    const sourceList = useSelector(getSourcesListSelector);
    const loadingSourceList = useSelector(getSourcesListPendingSelector)

    // source details
    const sourceDetails = useSelector(getSourceSelector);

    // deleted source
    const deletedSource = useSelector(deleteSourceSelector);

    const params: ISearchParam = {
        skip: page * rowsPerPage,
        top: rowsPerPage,
        count: true,
        search: searchTerm,
        layerType: layerTypes,
        accessControl: accessControls,
        dataStewards: dataStewards,
        owner: owners,
        orderby: `${orderBy} ${order}`,
        spokeName: spokeName

    }

    useEffect(() => {
        window.scrollTo(0, 0)
        dispatch(fetchSourcesReq(params));

    }, [dispatch, page, rowsPerPage, deleteSourceSelector, searchTerm, layerTypes, accessControls, owners, orderBy, dataStewards, order, spokeName]);


    useEffect(() => {

        let data: IDataSource[] = (sourceList.sources);
        setDataRows(data);
        setTotalRows(sourceList.count);

    }, [sourceList]);


    useEffect(() => {

        let isDetailExists = false;
        if (sourceDetails) {
            isDetailExists = rowDetails.filter((x) => x && x.sourceId === sourceDetails!.sourceId).length > 0;
            if (!isDetailExists) {
                setRowDetails([...rowDetails, sourceDetails!]);
            }
        }

    }, [sourceDetails, rowDetails]);

    // table related events 

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: string,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property as keyof IDataSource);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = dataRows.map((n: { name: any; }) => n.name);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const deleteAllClick = () => {
        setSelected([]);
    }

    //    ************ spoke based access controls ****************
    const userProfile: IProfile[] = useSelector(getProfileSelector);
    const getSpokeList = useSelector(fetchSpokeSelector);

    const [spokeProfile, setSpokeProfile] = useState<any>();
    const [spokeList, setSpokeList] = useState<any>();

    const spokeparams: ISpokeSearchParams = {
        count: true,
        top: rowsPerPage,
        skip: 0,
        name: [],
        owner: [],
    };

    useEffect(() => {
        dispatch(fetchSpokesRequest(spokeparams));
    }, []);


    useEffect(() => {
        if (userProfile && userProfile.length > 0) {
            setSpokeProfile(userProfile);
        }
    }, [userProfile]);

    useEffect(() => {
        if (getSpokeList && getSpokeList.length > 0) {
            setSpokeList(getSpokeList);
        }
    }, [getSpokeList]);
    let spoke: ISpokeDetails[];
    const isSpokeAdmin = (row: IDataSource) => {

        let spokeName: string = row.spokeName;

        // filter spoke list based on row spoke name: returns single spoke
        if (spokeList) {
            spoke = spokeList.filter((x: any) => x.name === spokeName)

        }

        // check if spoke exists in the users profile
        let x;
        if (spoke && spoke.length > 0 && spokeProfile) {
            x = spokeProfile.filter((x: any) => x.adGroupName === spoke[0].adminGroupName)

        }

        // if the spoke admin group name matches user's group name ==> user is spoke admin
        if (x && x.length > 0) return true
        else return false

    }

    //    ************ end spoke based access controls ****************

    const filterResults = (event: React.MouseEvent<HTMLButtonElement>, filters: string[]) => {

        let newAccess: string[] = [];
        let newLayers: string[] = [];
        let newOwners: string[] = [];
        let newStewards: string[] = [];
        let newSpoke: string[] = [];


        filters.forEach(item => {
            let filter: string[] = item.split(": ");
            switch (filter[0]) {
                case 'accessControl':
                    newAccess.push(filter[1]);
                    setAccessControls(newAccess);
                    params.accessControl = newAccess;
                    break;
                case 'layerType':
                    newLayers.push(filter[1]);
                    setLayerTypes(newLayers);
                    params.layerType = newLayers;
                    break;
                case 'owner':
                    newOwners.push(filter[1]);
                    setOwners(newOwners);
                    params.owner = newOwners;
                    break;
                case 'dataStewards':
                    newStewards.push(filter[1]);
                    setDataStewards(newStewards);
                    params.dataStewards = newStewards;
                    break;
                case 'spokeName':
                    newSpoke.push(filter[1]);
                    setSpokeName(newSpoke);
                    params.spokeName = newSpoke;
                    break;
                default:
                    break;
            }
        });

        setFilters(filters);
        setOpenFilters(true);
        setPage(0);

        dispatch(fetchSourcesReq(params));

    }

    const refreshPage = () => {

        setLayerTypes([]);
        params.layerType = [];

        setAccessControls([]);
        params.accessControl = [];

        setOwners([])
        params.owner = [];

        setDataStewards([])
        params.dataStewards = [];

        setSpokeName([])
        params.spokeName = [];

        setFilters([]);
        setOpenFilters(false);

        setOrder('asc')
        setOrderBy("description")

        dispatch(fetchSourcesReq(params));

    }

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPage(0);
        setSearchTerm(e.target.value);

    }

    const handleSearchDebounce = useMemo(() => {
        return debounce(handleSearch, SEARCH_DEBOUNCE_TIME_MILLIS);
    }, []);

    const searchEl = () => {
        return (
            <TextField
                id="source-search"
                label="Search Sources"
                type="search"
                fullWidth={true}
                onChange={handleSearchDebounce}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }}
            />
        );
    };

    const handleExpand = (row: IDataSource) => {
        let isExist = rowDetails.find((x) => x && x.name === row.name);

        if (!isExist) {
            // dispatch get source details action
            dispatch(fetchSourceDetailsReq(row.sourceId));
        }
    }

    const handleSourceView = (row: IDataSource,event:React.MouseEvent<HTMLButtonElement>) => {
        const url = `/ViewDataSource/${row.sourceId}`;
        if(event.ctrlKey){
            window.open(url,'_blank')
        }
        else{
        navigate(url);
        }
    }
    const handleSourceEdit = (row: IDataSource,event:React.MouseEvent<HTMLButtonElement>) => {
        const url = `/EditDataSource/${row.sourceId}`;
        if(event.ctrlKey){
            window.open(url,'_blank');
        }
        else{
        navigate(url);
        }
    }

    const confirmSourceDelete = (row: IDataSource) => {
        setOpen(true);
        setRowID(row.sourceId);
        setRowName(row.name);
    }

    const handleSourceDelete = () => {
        setOpen(false);
        setDeletedIds(item => [...item, rowID.toString()]);
        setDeletedIdLoading(rowID.toString())
        dispatch(deleteSourceReq(rowID));

    }

    const handleClose = () => {
        setOpen(false);
    }

    const tableToolBar = () => {
        return (
            <EnhancedTableToolbar numSelected={selected.length} title={'Data Sources'} headCells={headCells} filterCells={filterCells} deleteAll={deleteAllClick}
                filterResults={filterResults} refreshPage={refreshPage} currentFilters={filters} openToolbar={openFilters} />
        );
    }

    const tableHeader = () => {
        return (
            <EnhancedTableHead
                headCells={headCells}
                numSelected={selected.length}
                selectable={allSelectable}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={totalRows}
            />
        );
    }

    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalRows) : 0;

    const tableRows = () => {

        return (
            <>
                { dataRows?.length > 0 && dataRows.slice().sort(getComparator(order, orderBy))
                    .map((row: IDataSource, index: number) => {
                        const isItemSelected = isSelected(row.name);
                        const details = rowDetails.find((x) => x && x.name === row.name);
                        return (
                            <Row row={row}
                                key={row.name}
                                headCells={headCells}
                                selectable={selectable}
                                // canEdit={isAdmin || isSpokeAdmin(row)} based on garry's feedback - 21/09
                                // canDelete={isAdmin || isSpokeAdmin(row)} based on garry's feedback - 21/09
                                canEdit={isAdmin}
                                canDelete={isAdmin}
                                canView={true}                            
                                canExpand={true}
                                isItemSelected={isItemSelected}
                                handleExpand={handleExpand}
                                handleView={handleSourceView}
                                handleEdit={handleSourceEdit}
                                handleDelete={confirmSourceDelete}
                                loadingIcon={deletePending}
                                deleteId={deletedSource}
                                deletedRows={deletedIds}
                                deletedIdLoading={deletedIdLoading}
                            >
                                {details && rowDetails.length > 0 &&
                                    <Box className="expand-details" sx={{ margin: 1 }}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={6}>
                                                <div><span className="title">Name : </span><span>{details.name ?? "None"}</span></div>
                                                <div><span className="title">Description : </span><span>{details?.business?.description ?? "None"}</span></div>
                                                <div><span className="title">Data Stewards : </span><span>{details?.business?.dataStewards ?? "None"}</span></div>
                                                <div><span className="title">Secret Name : </span><span>{details.secretName ?? "None"}</span></div>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <div><span className="title">DbType : </span><span>{details.technical?.dbType ?? "None"}</span></div>
                                                <div><span className="title">Security Classification : </span><span>{details.governance?.securityClassification ? getSecurityClassificationText(details.governance?.securityClassification) : "none"}</span></div>
                                                <div><span className="title">Privacy Classification : </span><span>{details.governance?.privacyClassification ? getPrivacyClassificationText(details.governance?.privacyClassification) : "none"}</span></div>
                                                <div><span className="title">Access Controls : </span><span>{details?.governance?.accessControl ?? "None"}</span></div>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                }
                            </Row>
                        );
                    })
                }
                {emptyRows > 0 && emptyRows < 10 && (
                    <TableRow
                        style={{
                            height: (53) * emptyRows,
                        }}
                    >
                        <TableCell colSpan={6} />
                    </TableRow>
                )}

            </>
        );

    }


    const listGrid = () => {

        return (
            <>
                {/* adding skeleton screen on loading */}
                {loadingSourceList ?
                    <Grid container width="100%">
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                    </Grid>

                    :
                    <EnhancedTable
                        totalRows={totalRows}
                        tableToolBar={tableToolBar()}
                        tableHeader={tableHeader()}
                        tableRows={tableRows()}
                        pagination={true}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                }
            </>
        );
    }

    return (
        <>
            <SearchListGrid searchEl={searchEl()} children={listGrid()} />

            <CustomizedDialogs
                dialogHeight=''
                modalSize="md"
                key="target"
                modalTitle="Delete Confirmation"
                isOpen={open}
                onClose={handleClose}
                footerAction={
                    <Stack direction="row" spacing={2}>
                        <Button id="266810ee-717e-4ca2-9b83-86ab13a0d778" color="primary" onClick={handleSourceDelete} autoFocus>
                            Yes
                    </Button>
                        <Button id="16468233-559d-4114-872d-63235116bd40" color="primary" onClick={handleClose}>
                            No
                    </Button>
                    </Stack>
                }
            >
                <> Are you sure you want to delete {rowName}?</>
            </CustomizedDialogs>
        </>
    );

};

export default SourceExplorer;